import React from 'react';
import {
  Heading, HStack, Icon, Pressable, Text, VStack,
} from '@gluestack-ui/themed';
import { ArrowRight, ListChecks } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { BulletItem } from '@/common/components/BulletList';
import { OralExamDryRunBullet } from '@/student/course/components/OralExamPracticeEntry';
import { formatEnglishTime } from '@/common/utils/formatEnglishDateTime';
import { ORAL_EXAM_RECOMMENDED_ARRIVAL_MINUTES } from '@/common/utils/oralExam/studentJoinWindow';

export type OralExamBeforeExamChecklistProps = {
  /** Confirmed booking start (ISO). Omit when the student has not booked yet. */
  bookingStartIso?: string | null;
  /** Number of questions in the pool, shown on the questions button. */
  questionCount?: number;
  /** Opens the question pool. Omit where the pool is already on screen (task page). */
  onOpenQuestions?: () => void;
  /** Offers the dry run as an optional line. Omit where it cannot start. */
  dryRunTaskId?: string;
};

/**
 * OralExamBeforeExamChecklist
 * ---------------------------
 * Preparation checklist for graded oral exams, below the registration. The
 * parent hides it once the exam has been taken. The dry run is an optional
 * line, not an instruction. The last step is preparing for the questions, so
 * the way to the question pool lives here.
 * The join-by clock intentionally uses the browser timezone, matching the
 * appointment time displayed beside it.
 */
export function OralExamBeforeExamChecklist({
  bookingStartIso,
  questionCount = 0,
  onOpenQuestions,
  dryRunTaskId,
}: OralExamBeforeExamChecklistProps) {
  const { t } = useTranslation();

  const joinByLabel = bookingStartIso
    ? formatEnglishTime(dayjs(bookingStartIso).subtract(ORAL_EXAM_RECOMMENDED_ARRIVAL_MINUTES, 'minute').toDate())
    : null;

  return (
    <VStack
      space="sm"
      testID="oral-exam-before-exam-checklist"
      accessibilityRole="none"
    >
      <Heading size="sm">{t('oralExam.beforeExam.title')}</Heading>
      <VStack space="sm">
        <BulletItem size="sm">
          {joinByLabel
            ? t('oralExam.beforeExam.joinBy', { joinBy: joinByLabel })
            : t('oralExam.beforeExam.joinEarly')}
        </BulletItem>
        <BulletItem size="sm">{t('oralExam.beforeExam.checkIn')}</BulletItem>
        {dryRunTaskId ? <OralExamDryRunBullet taskId={dryRunTaskId} /> : null}
        <BulletItem size="sm">{t('oralExam.beforeExam.prepare')}</BulletItem>
      </VStack>
      {onOpenQuestions ? (
        <Pressable
          onPress={onOpenQuestions}
          accessibilityRole="button"
          testID="oral-exam-open-questions"
          mt="$1"
        >
          {({ pressed }) => (
            <HStack
              alignItems="center"
              space="sm"
              px="$4"
              py="$3"
              borderRadius="$md"
              borderWidth={1}
              borderColor="$borderBrand"
              bg="$bgBrandSubtle"
              opacity={pressed ? 0.8 : 1}
            >
              <Icon as={ListChecks} size="sm" color="$textBrand" />
              <Text flex={1} size="sm" fontFamily="$heading" color="$textBrand">
                {t('oralExam.beforeExam.openQuestions')}
              </Text>
              {questionCount > 0 ? (
                <Text size="sm" color="$textBrand">{questionCount}</Text>
              ) : null}
              <Icon as={ArrowRight} size="sm" color="$textBrand" />
            </HStack>
          )}
        </Pressable>
      ) : null}
    </VStack>
  );
}
